/**
 * Wishlist — localStorage + Worker hybrid with Optimistic UI
 *
 * - Product detail page: toggle heart button
 * - Wishlist page: render grid from stored handles
 * - Worker sync: ログイン時に localStorage → Metafields へ一括マージ
 *
 * ストレージ:
 *   非ログイン → localStorage のみ（ローカル完結）
 *   ログイン時 → localStorage を Worker 経由で Metafields にマージ
 *   ログイン済み → Worker (Metafields) が SSOT、localStorage はキャッシュ
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'anok-wishlist';
  var MAX_ITEMS = 50;
  var WORKER_URL = '/apps/features/wishlist';  // Shopify App Proxy 経由
  var LOGIN_TOAST_MESSAGE = 'お気に入り商品を保存するにはログインしてください。';

  /**
   * ログインゲート判定。フラグは theme.shop.liquid の inline script が
   * `window.anokCustomerLoggedIn` に出力する（defer 実行の本 JS より先に評価済み）。
   * 未ログイン時はお気に入りを保存せず、ログイン案内トーストのみ表示する。
   * localStorage は消さない（ログイン後の Worker reconcile フローは既存のまま）
   */
  function isLoggedIn() {
    return window.anokCustomerLoggedIn === true;
  }

  /* ------------------------------------------------------------------ */
  /*  Worker API wrapper                                                 */
  /* ------------------------------------------------------------------ */

  /**
   * Worker API を呼び出す統一ラッパー
   * 非ログイン時は 401 が返る → catch で無視
   */
  async function workerFetch(method, path, body) {
    var options = { method: method, headers: {} };
    var url = WORKER_URL + path;

    if (body) {
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }

    var response = await fetch(url, options);
    if (!response.ok) throw new Error('Worker error: ' + response.status);
    return response.json();
  }

  /* ------------------------------------------------------------------ */
  /*  localStorage helpers                                               */
  /* ------------------------------------------------------------------ */

  function getAll() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      localStorage.removeItem(STORAGE_KEY);
      return [];
    }
  }

  function save(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      /* Safari private mode — silently fail */
    }
  }

  function makeKey(handle, variantId) {
    return handle + ':' + variantId;
  }

  function parseKey(key) {
    var parts = key.split(':');
    return {
      handle: parts[0],
      variantId: parts[1] || null
    };
  }

  function has(handle, variantId) {
    var key = makeKey(handle, variantId);
    return getAll().indexOf(key) !== -1;
  }

  /**
   * お気に入り追加（Optimistic UI: 即座に localStorage + UI 更新）
   * Worker 呼び出しは fire-and-forget（失敗しても次回 reconcile で修復）
   */
  function add(handle, variantId) {
    var key = makeKey(handle, variantId);
    var list = getAll();
    if (list.indexOf(key) !== -1) return;
    list.push(key);
    if (list.length > MAX_ITEMS) list.shift();
    save(list);
    dispatch(list);

    // Fire-and-forget Worker call
    workerFetch('POST', '/add', { handle: handle, variant_id: variantId })
      .catch(function (e) { console.warn('Worker add failed (will reconcile):', e); });
  }

  /**
   * お気に入り削除（Optimistic UI）
   */
  function remove(handle, variantId) {
    var key = makeKey(handle, variantId);
    var list = getAll().filter(function (k) { return k !== key; });
    save(list);
    dispatch(list);

    // Fire-and-forget Worker call
    workerFetch('DELETE', '/remove', { handle: handle, variant_id: variantId })
      .catch(function (e) { console.warn('Worker remove failed (will reconcile):', e); });
  }

  /**
   * お気に入りトグル（追加⇔削除）。商品カード等の「追加」ボタン用。
   */
  function toggle(handle, variantId) {
    if (has(handle, variantId)) {
      remove(handle, variantId);
      return false;
    }
    add(handle, variantId);
    return true;
  }

  function dispatch(items) {
    document.dispatchEvent(new CustomEvent('anok:wishlist:update', {
      detail: { items: items }
    }));
  }

  /* ------------------------------------------------------------------ */
  /*  Worker reconciliation                                              */
  /* ------------------------------------------------------------------ */

  /**
   * Worker と localStorage を照合し、差分があればマージ
   * ログイン済みの場合のみ有効（非ログインは Worker が 401 → catch）
   */
  async function reconcileWithWorker() {
    try {
      var data = await workerFetch('GET', '', null);
      var workerItems = data.wishlist || [];
      var localItems = getAll();

      // Union merge
      var merged = localItems.slice();
      for (var i = 0; i < workerItems.length; i++) {
        if (merged.indexOf(workerItems[i]) === -1) {
          merged.push(workerItems[i]);
        }
      }

      // If different, update both
      if (merged.length !== localItems.length || merged.length !== workerItems.length) {
        save(merged);
        dispatch(merged);
        workerFetch('POST', '/sync', { items: merged })
          .catch(function (e) { console.warn('Worker sync failed:', e); });
      }
    } catch (e) {
      console.warn('Reconcile failed (using cache):', e);
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Login sync                                                         */
  /* ------------------------------------------------------------------ */

  /**
   * ログイン時に localStorage の内容を Worker に一括マージ
   * 直列 POST の代わりに /sync で一回で送る
   */
  async function syncOnLogin() {
    var localItems = getAll();

    try {
      if (localItems.length > 0) {
        // localStorage にデータがあれば Worker にマージ
        var data = await workerFetch('POST', '/sync', { items: localItems });
        var merged = data.wishlist || [];
        save(merged);
        dispatch(merged);
      } else {
        // localStorage が空なら Worker から取得
        var data = await workerFetch('GET', '', null);
        var workerItems = data.wishlist || [];
        if (workerItems.length > 0) {
          save(workerItems);
          dispatch(workerItems);
        }
      }
    } catch (e) {
      console.warn('Sync on login failed (using cache):', e);
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Product detail page — toggle button                                */
  /* ------------------------------------------------------------------ */

  function initToggleButton() {
    var btn = document.querySelector(
      '.anok-product-info__wishlist-btn[data-product-handle]'
    );
    if (!btn) return;

    var handle = btn.getAttribute('data-product-handle');
    var getCurrentVariantId = function () {
      var variantInput = document.querySelector(
        'input[name="id"]:checked, .anok-product-info__form-variant-id'
      );
      return variantInput ? variantInput.value : null;
    };

    function sync() {
      var variantId = getCurrentVariantId();
      // 未ログイン時は localStorage に残っていても active 表示しない
      var active = isLoggedIn() && has(handle, variantId);
      btn.classList.toggle('anok-product-info__wishlist-btn--active', active);
      btn.setAttribute('aria-pressed', String(active));
      btn.setAttribute(
        'aria-label',
        active ? 'お気に入りから削除' : 'お気に入りに追加'
      );
    }

    sync();

    btn.addEventListener('click', function () {
      if (!isLoggedIn()) {
        showToast(LOGIN_TOAST_MESSAGE);
        return;
      }
      var variantId = getCurrentVariantId();
      if (has(handle, variantId)) {
        remove(handle, variantId);
      } else {
        add(handle, variantId);
      }
      sync();
    });

    // Listen to variant changes
    var variantInputs = document.querySelectorAll('.anok-product-info__variant-option input[type="radio"]');
    variantInputs.forEach(function (input) {
      input.addEventListener('change', sync);
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Wishlist page — render grid                                        */
  /* ------------------------------------------------------------------ */

  function formatPrice(priceInYen) {
    return '\u00a5' + parseInt(priceInYen, 10).toLocaleString('ja-JP');
  }

  // wishlistKey -> { product, variant } — bindCartButtons がクリック時に参照
  var cardDataMap = {};

  function showToast(message) {
    var container = document.querySelector('.anok-toast-container');
    if (!container) {
      container = document.createElement('div');
      container.className = 'anok-toast-container';
      document.body.appendChild(container);
    }
    var toast = document.createElement('div');
    toast.className = 'anok-toast';
    toast.textContent = message;
    container.appendChild(toast);
    requestAnimationFrame(function () {
      toast.classList.add('anok-toast--show');
    });
    setTimeout(function () {
      toast.classList.remove('anok-toast--show');
      setTimeout(function () { toast.remove(); }, 300);
    }, 2500);
  }

  function createCard(product, variant, wishlistKey) {
    cardDataMap[wishlistKey] = { product: product, variant: variant };
    var handle = product.handle;
    var image = product.images && product.images[0]
      ? product.images[0].src
      : '';
    var title = product.title || '';
    var variantTitle = variant.title || '';
    if (variantTitle === 'Default Title') variantTitle = '';
    var price = formatPrice(variant.price);
    var productUrl = '/products/' + handle + '?variant=' + variant.id;

    var content = document.querySelector('.anok-wishlist__content');
    var cartIconUrl = content ? content.getAttribute('data-cart-icon-url') : '';
    var heartFilledIconUrl = content ? content.getAttribute('data-heart-filled-icon-url') : '';

    var card = document.createElement('div');
    card.className = 'anok-wishlist__card';
    card.setAttribute('role', 'listitem');
    card.setAttribute('data-wishlist-key', wishlistKey);
    card.setAttribute('data-product-url', productUrl);

    var imageHtml = image
      ? '<img src="' + image + '" alt="' + title.replace(/"/g, '&quot;') + '" loading="lazy" width="240" height="245">'
      : '<div class="anok-wishlist__card-no-image"></div>';

    var cartIcon = '<img src="' + cartIconUrl + '" alt="" class="anok-wishlist__cart-btn-icon">';
    var heartIcon = '<img src="' + heartFilledIconUrl + '" alt="" class="anok-wishlist__heart-icon">';

    card.innerHTML =
      '<div class="anok-wishlist__card-image-container">' +
        imageHtml +
        '<button type="button" class="anok-wishlist__cart-btn" data-handle="' + handle + '" data-variant-id="' + variant.id + '">' +
          cartIcon +
          '<span class="anok-wishlist__cart-btn-text">カートに入れる</span>' +
        '</button>' +
      '</div>' +
      '<div class="anok-wishlist__card-info">' +
        '<div class="anok-wishlist__card-text">' +
          '<h3 class="anok-wishlist__card-title">' + title + '</h3>' +
          '<div class="anok-wishlist__card-meta">' +
            (variantTitle ? '<span class="anok-wishlist__card-variant">' + variantTitle + '</span>' : '') +
            (price ? '<span class="anok-wishlist__card-price">' + price + '</span>' : '') +
          '</div>' +
        '</div>' +
        '<button type="button" class="anok-wishlist__heart-btn" data-wishlist-key="' + wishlistKey + '" aria-label="お気に入りから削除">' +
          heartIcon +
        '</button>' +
      '</div>';

    return card;
  }

  function initWishlistPage() {
    var grid = document.querySelector('.anok-wishlist__grid');
    if (!grid) return;

    var loading = document.querySelector('.anok-wishlist__loading');
    var empty = document.querySelector('.anok-wishlist__empty');
    // 未ログイン時は常に空扱い（Liquid 側でも grid ごと非表示にしているが防御的に二重化）
    var items = isLoggedIn() ? getAll() : [];

    function showEmpty() {
      if (empty) empty.hidden = false;
      grid.hidden = true;
      var footer = document.querySelector('.anok-wishlist__footer');
      if (footer) footer.hidden = true;
    }

    function hideLoading() {
      if (loading) loading.hidden = true;
    }

    if (items.length === 0) {
      hideLoading();
      showEmpty();
      return;
    }

    var fetches = items.map(function (item) {
      var parsed = parseKey(item);
      return fetch('/products/' + parsed.handle + '.json')
        .then(function (res) {
          if (!res.ok) throw new Error(res.status);
          return res.json();
        })
        .then(function (data) {
          var product = data.product;
          var variant = product.variants.find(function (v) {
            return String(v.id) === parsed.variantId;
          });
          return {
            product: product,
            variant: variant || product.variants[0],
            wishlistKey: item
          };
        })
        .catch(function () {
          remove(parsed.handle, parsed.variantId);
          return null;
        });
    });

    Promise.allSettled(fetches).then(function (results) {
      hideLoading();

      var items = results
        .map(function (r) { return r.value; })
        .filter(Boolean);

      if (items.length === 0) {
        showEmpty();
        return;
      }

      if (empty) empty.hidden = true;
      grid.hidden = false;

      items.forEach(function (item) {
        grid.appendChild(createCard(item.product, item.variant, item.wishlistKey));
      });

      var footer = document.querySelector('.anok-wishlist__footer');
      if (footer) footer.hidden = false;

      bindCardClick(grid);
      bindCartButtons(grid);
      bindDeleteButtons(grid, showEmpty);
    });

    // After grid render, reconcile in background (logged-in only)
    reconcileWithWorker();
  }

  function bindCardClick(grid) {
    grid.addEventListener('click', function (e) {
      if (e.target.closest('button')) return;

      var card = e.target.closest('.anok-wishlist__card');
      if (!card) return;

      var productUrl = card.getAttribute('data-product-url');
      if (productUrl) {
        window.location.href = productUrl;
      }
    });
  }

  function bindCartButtons(grid) {
    grid.addEventListener('click', function (e) {
      var cartBtn = e.target.closest('.anok-wishlist__cart-btn');
      if (!cartBtn) return;

      e.preventDefault();
      e.stopPropagation();

      var card = cartBtn.closest('.anok-wishlist__card');
      var wishlistKey = card ? card.getAttribute('data-wishlist-key') : '';
      var data = cardDataMap[wishlistKey];
      if (!data || !window.anokCartDrawer) return;

      // variant ID だけ渡す。表示内容はサーバーが返すドロワー HTML が持つ。
      window.anokCartDrawer.addItem(data.variant.id, 1);
    });
  }

  function bindDeleteButtons(grid, showEmpty) {
    grid.addEventListener('click', function (e) {
      var heartBtn = e.target.closest('.anok-wishlist__heart-btn');
      if (!heartBtn) return;

      e.stopPropagation();

      var card = heartBtn.closest('.anok-wishlist__card');
      if (!card) return;

      var wishlistKey = heartBtn.getAttribute('data-wishlist-key');
      var parsed = parseKey(wishlistKey);
      remove(parsed.handle, parsed.variantId);
      showToast('お気に入りリストから商品を削除しました');

      card.classList.add('anok-wishlist__card--removing');
      setTimeout(function () {
        card.remove();
        if (grid.querySelectorAll('.anok-wishlist__card').length === 0) {
          showEmpty();
        }
      }, 300);
    });
  }

  function initClearAllButton() {
    var clearBtn = document.querySelector('.anok-wishlist__clear-all');
    var modal = document.querySelector('.anok-wishlist__confirm');
    if (!clearBtn || !modal) return;

    var deleteBtn = modal.querySelector('.anok-wishlist__confirm-delete');
    var cancelBtn = modal.querySelector('.anok-wishlist__confirm-cancel');

    function onKeydown(e) {
      if (e.key === 'Escape') closeConfirm();
    }

    function openConfirm() {
      modal.hidden = false;
      document.addEventListener('keydown', onKeydown);
      // 安全側（いいえ）に初期フォーカス
      if (cancelBtn) cancelBtn.focus();
    }

    function closeConfirm() {
      modal.hidden = true;
      document.removeEventListener('keydown', onKeydown);
      clearBtn.focus();
    }

    clearBtn.addEventListener('click', openConfirm);

    // 「いいえ」/ 閉じるボタン / overlay クリックで閉じる
    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-confirm-close]')) closeConfirm();
    });

    if (deleteBtn) deleteBtn.addEventListener('click', function () {
      save([]);
      dispatch([]);

      // Clear Worker (fire-and-forget, logged-in only)
      workerFetch('DELETE', '/clear', {})
        .catch(function (e) { console.warn('Worker clear failed:', e); });

      var grid = document.querySelector('.anok-wishlist__grid');
      if (grid) grid.innerHTML = '';

      var empty = document.querySelector('.anok-wishlist__empty');
      if (empty) empty.hidden = false;
      if (grid) grid.hidden = true;

      var footer = document.querySelector('.anok-wishlist__footer');
      if (footer) footer.hidden = true;

      closeConfirm();
      showToast('お気に入りリストの商品を全て削除しました');
    });
  }

  /* ------------------------------------------------------------------ */
  /*  商品カードのお気に入りボタン（collections/all グリッド等）          */
  /* ------------------------------------------------------------------ */

  function syncCardButtons() {
    var buttons = document.querySelectorAll('[data-anok-wishlist-toggle]');
    buttons.forEach(function (btn) {
      var handle = btn.getAttribute('data-product-handle');
      var variantId = btn.getAttribute('data-variant-id');
      // 未ログイン時は localStorage に残っていても active 表示しない
      var active = isLoggedIn() && has(handle, variantId);
      btn.classList.toggle('anok-product-card__favorite--active', active);
      btn.setAttribute('aria-pressed', String(active));
      btn.setAttribute('aria-label', active ? 'お気に入りから削除' : 'お気に入りに追加');
      var icon = btn.querySelector('img');
      if (icon) {
        var filled = btn.getAttribute('data-heart-filled');
        var outline = btn.getAttribute('data-heart-outline');
        var next = active ? filled : outline;
        if (next && icon.getAttribute('src') !== next) icon.setAttribute('src', next);
      }
    });
  }

  function initCardButtons() {
    syncCardButtons();

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-anok-wishlist-toggle]');
      if (!btn) return;
      e.preventDefault();
      if (!isLoggedIn()) {
        showToast(LOGIN_TOAST_MESSAGE);
        return;
      }
      toggle(btn.getAttribute('data-product-handle'), btn.getAttribute('data-variant-id'));
      syncCardButtons();
    });

    // 他タブ / PDP / 別カードの変更を反映
    document.addEventListener('anok:wishlist:update', syncCardButtons);
  }

  /* ------------------------------------------------------------------ */
  /*  Public API — カード等から add / toggle を利用するための最小公開      */
  /* ------------------------------------------------------------------ */

  window.anokWishlist = {
    add: add,
    remove: remove,
    toggle: toggle,
    has: has,
    getAll: getAll
  };

  /* ------------------------------------------------------------------ */
  /*  Init                                                               */
  /* ------------------------------------------------------------------ */

  document.addEventListener('DOMContentLoaded', function () {
    if (document.querySelector('.anok-product-info__wishlist-btn[data-product-handle]')) {
      initToggleButton();
    }
    if (document.querySelector('[data-anok-wishlist-toggle]')) {
      initCardButtons();
    }
    if (document.querySelector('.anok-wishlist__grid')) {
      initWishlistPage();
      initClearAllButton();
    }

    // Login sync: localStorage → Worker に一括マージ
    var content = document.querySelector('.anok-wishlist__content');
    if (content && content.hasAttribute('data-logged-in')) {
      syncOnLogin();
    }
  });
})();